"use client";

import { motion } from "framer-motion";
import type { MusicDNAKey } from "@/lib/music-dna/analyzeMusicDNA";

interface MusicDNAAnimationProps {
  dnaType: MusicDNAKey;
  emoji: string;
  isRevealed: boolean;
}

export function MusicDNAAnimation({ dnaType, emoji, isRevealed }: MusicDNAAnimationProps) {
  // DNA 타입별 애니메이션 설정
  const animations = {
    감성파: {
      // 물결 효과
      animate: isRevealed
        ? {
            y: [0, -8, 0],
            scale: [1, 1.05, 1],
          }
        : { rotate: 12 },
      transition: {
        y: { duration: 2, repeat: Infinity, ease: "easeInOut" },
        scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
      },
      background: "bg-gradient-to-br from-blue-400/20 via-cyan-400/20 to-blue-500/20",
      glow: "shadow-[0_0_30px_rgba(59,130,246,0.3)]",
    },
    리듬메이커: {
      // 펄스 효과
      animate: isRevealed
        ? {
            scale: [1, 1.1, 1],
          }
        : { rotate: 12 },
      transition: {
        scale: { duration: 0.6, repeat: Infinity, ease: "easeInOut" },
      },
      background: "bg-gradient-to-br from-red-400/20 via-orange-400/20 to-red-500/20",
      glow: "shadow-[0_0_30px_rgba(239,68,68,0.3)]",
    },
    섬세파: {
      // 부드러운 회전
      animate: isRevealed
        ? {
            rotate: [0, 5, -5, 0],
            scale: [1, 1.02, 1],
          }
        : { rotate: 12 },
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
      },
      background: "bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-purple-500/20",
      glow: "shadow-[0_0_30px_rgba(168,85,247,0.3)]",
    },
    내면파: {
      // 은은한 빛
      animate: isRevealed
        ? {
            opacity: [0.8, 1, 0.8],
            scale: [1, 1.03, 1],
          }
        : { rotate: 12 },
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      },
      background: "bg-gradient-to-br from-indigo-400/20 via-blue-400/20 to-indigo-500/20",
      glow: "shadow-[0_0_30px_rgba(99,102,241,0.3)]",
    },
    에너지파: {
      // 번개 효과 (빠른 펄스)
      animate: isRevealed
        ? {
            scale: [1, 1.15, 1],
            rotate: [0, 3, -3, 0],
          }
        : { rotate: 12 },
      transition: {
        scale: { duration: 0.4, repeat: Infinity, ease: "easeOut" },
        rotate: { duration: 0.4, repeat: Infinity, ease: "easeInOut" },
      },
      background: "bg-gradient-to-br from-yellow-400/20 via-amber-400/20 to-yellow-500/20",
      glow: "shadow-[0_0_40px_rgba(250,204,21,0.4)] animate-pulse",
    },
    감각파: {
      // 그라데이션 웨이브 (배너 스타일!)
      animate: isRevealed
        ? {
            y: [0, -10, 0],
            x: [0, 5, -5, 0],
            scale: [1, 1.08, 1],
          }
        : { rotate: 12 },
      transition: {
        y: { duration: 2.5, repeat: Infinity, ease: "easeInOut" },
        x: { duration: 3, repeat: Infinity, ease: "easeInOut" },
        scale: { duration: 2.5, repeat: Infinity, ease: "easeInOut" },
      },
      background: "bg-gradient-to-br from-pink-400/20 via-purple-400/20 to-cyan-400/20",
      glow: "shadow-[0_0_40px_rgba(236,72,153,0.4)]",
    },
    콘셉트파: {
      // 회전 효과
      animate: isRevealed
        ? {
            rotate: [0, 360],
            scale: [1, 1.05, 1],
          }
        : { rotate: 12 },
      transition: {
        rotate: { duration: 8, repeat: Infinity, ease: "linear" },
        scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
      },
      background: "bg-gradient-to-br from-teal-400/20 via-emerald-400/20 to-teal-500/20",
      glow: "shadow-[0_0_30px_rgba(20,184,166,0.3)]",
    },
    즉흥파: {
      // 불꽃 효과 (불규칙한 움직임)
      animate: isRevealed
        ? {
            y: [0, -12, -5, -15, 0],
            scale: [1, 1.1, 0.95, 1.08, 1],
            rotate: [0, 5, -3, 2, 0],
          }
        : { rotate: 12 },
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
      background: "bg-gradient-to-br from-orange-400/20 via-red-400/20 to-orange-500/20",
      glow: "shadow-[0_0_35px_rgba(249,115,22,0.4)]",
    },
  };

  const config = animations[dnaType] || animations.감각파;

  return (
    <motion.div
      className={`flex h-20 w-20 items-center justify-center rounded-2xl border border-white/20 ${config.background} ${config.glow} backdrop-blur-sm text-4xl sm:h-24 sm:w-24 sm:text-5xl relative overflow-hidden`}
      animate={config.animate}
      transition={config.transition as any}
    >
      {/* 배경 애니메이션 레이어 */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
      
      {/* 이모지 */}
      <span className="relative z-10">{emoji}</span>
    </motion.div>
  );
}






